import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from '../../../services/api.service';
import { LoaderComponent } from '../../layout/loader/loader.component';
import { ModalHeaderComponent } from '../../modals/modal-header/modal-header.component';
import { ConfirmDeleteComponent } from '../../confirm-delete/confirm-delete.component';
import { InformationModalComponent } from '../../information-modal/information-modal.component';

@Component({
  selector: 'app-order-shipment-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LoaderComponent,
    ModalHeaderComponent
  ],
  templateUrl: './order-shipment-form.component.html',
  styleUrl: './order-shipment-form.component.scss'
})
export class OrderShipmentFormComponent implements OnInit {
  @Input() order: any = {};
  @Input() orderItems: any[] = [];
  @Input() shipment: any | null = null;

  modalTitle: string = 'Verzending aanmaken';
  modalStyle: boolean = true;
  loading: boolean = false;
  itemsLoading: boolean = false;
  errorMessage: string = '';

  shipmentStatuses: string[] = ['PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'];
  shipmentItems: any[] = [];
  newItem: any = {
    order_item_id: '',
    quantity: 1,
  };

  item: any = {
    order_id: null,
    status: 'PENDING',
    carrier: '',
    tracking_number: '',
    shipping_name: '',
    shipping_address: '',
    shipping_postal_code: '',
    shipping_city: '',
    shipping_country: '',
    notes: '',
  };

  constructor(
    private apiService: ApiService,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    if (this.shipment && this.shipment.id) {
      this.modalTitle = 'Verzending wijzigen';
      this.item = {
        ...this.item,
        ...this.shipment,
      };
      this.getShipmentItems();
      return;
    }

    this.item = {
      ...this.item,
      order_id: this.order.id,
      shipping_name: this.order.shipping_name || this.order.name || '',
      shipping_address: this.order.shipping_address || this.order.organisation_address || '',
      shipping_postal_code: this.order.shipping_postal_code || this.order.organisation_postal_code || '',
      shipping_city: this.order.shipping_city || this.order.organisation_city || '',
      shipping_country: this.order.shipping_country || this.order.organisation_country || '',
    };
  }

  save() {
    this.loading = true;
    this.errorMessage = '';

    if (this.item.id) {
      this.apiService.patch('/office/shipments/' + this.item.id, this.buildPayload()).then(() => {
        this.loading = false;
        this.activeModal.close(true);
      }).catch((e) => this.handleError(e, 'Verzending kon niet worden bijgewerkt.'));
      return;
    }

    this.apiService.post('/office/shipments', this.buildPayload()).then((response: any) => {
      this.item = {
        ...this.item,
        ...response,
      };
      this.modalTitle = 'Verzending wijzigen';
      this.loading = false;
      this.getShipmentItems();
    }).catch((e) => this.handleError(e, 'Verzending kon niet worden aangemaakt.'));
  }

  getShipmentItems() {
    if (!this.item.id) {
      return;
    }

    this.itemsLoading = true;
    this.apiService.get('/office/shipments/' + this.item.id + '/items').then((response: any) => {
      this.shipmentItems = Array.isArray(response) ? response : (response && response.data ? response.data : []);
      this.itemsLoading = false;
    }).catch(() => {
      this.shipmentItems = [];
      this.itemsLoading = false;
    });
  }

  addShipmentItem() {
    if (!this.item.id || !this.newItem.order_item_id || Number(this.newItem.quantity) < 1) {
      return;
    }

    this.itemsLoading = true;
    this.errorMessage = '';
    this.apiService.post('/office/shipments/' + this.item.id + '/items', {
      order_item_id: this.newItem.order_item_id,
      quantity: this.newItem.quantity,
    }).then(() => {
      this.newItem = { order_item_id: '', quantity: 1 };
      this.getShipmentItems();
    }).catch((e) => this.handleError(e, 'Artikel kon niet aan de verzending worden toegevoegd.', false));
  }

  deleteShipmentItem(shipmentItem: any) {
    this.itemsLoading = true;
    this.errorMessage = '';
    this.apiService.delete('/office/shipments/items/' + shipmentItem.id).then(() => {
      this.getShipmentItems();
    }).catch((e) => this.handleError(e, 'Artikel kon niet uit de verzending worden verwijderd.', false));
  }

  deleteShipment() {
    if (!this.item.id) {
      return;
    }

    const modalRef = this.modalService.open(ConfirmDeleteComponent, { size: 'md' });
    modalRef.componentInstance.modalStyle = true;
    modalRef.componentInstance.message = 'Weet je zeker dat je deze verzending wilt verwijderen?';
    modalRef.result.then((result) => {
      if (!result) {
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      this.apiService.delete('/office/shipments/' + this.item.id).then(() => {
        this.loading = false;
        this.activeModal.close(true);
      }).catch((e) => this.handleError(e, 'Verzending kon niet worden verwijderd.'));
    });
  }

  sendMail() {
    if (!this.item.id) {
      return;
    }

    const modalRef = this.modalService.open(InformationModalComponent, { size: 'xl' });
    modalRef.componentInstance.title = 'Verstuur verzendmail';
    modalRef.componentInstance.message = 'Wil je de verzendmail versturen?';
    modalRef.componentInstance.cancelButtonText = 'Annuleer';
    modalRef.componentInstance.confirmButtonText = 'Verstuur';
    modalRef.componentInstance.loader = true;

    this.apiService.getText('/office/shipments/' + this.item.id + '/preview-mail').then((response: any) => {
      modalRef.componentInstance.mailPreviewContent = response;
      modalRef.componentInstance.loader = false;
    }).catch(() => {
      modalRef.componentInstance.previewError = 'Fout bij het laden van de mail preview.';
      modalRef.componentInstance.loader = false;
    });

    modalRef.result.then((success) => {
      if (!success) {
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      this.apiService.post('/office/shipments/' + this.item.id + '/send-mail', {
        mail_content: ''
      }).then(() => {
        this.loading = false;
      }).catch((e) => {
        this.handleError(e, 'Verzendmail kon niet worden verstuurd.');
      });
    });
  }

  getShipmentStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      PENDING: 'Open',
      PROCESSING: 'Verwerken',
      SHIPPED: 'Verzonden',
      DELIVERED: 'Geleverd',
      CANCELLED: 'Geannuleerd',
    };
    return labels[status] || status;
  }

  getOrderItemLabel(orderItemId: any): string {
    const orderItem = (this.orderItems || []).find((item: any) => String(item.id) === String(orderItemId));
    if (!orderItem) {
      return 'Artikel #' + orderItemId;
    }

    return orderItem.name + ' (' + orderItem.quantity + 'x)';
  }

  private buildPayload() {
    return {
      order_id: this.order.id,
      status: this.item.status,
      carrier: this.item.carrier || '',
      tracking_number: this.item.tracking_number || '',
      shipping_name: this.item.shipping_name || '',
      shipping_address: this.item.shipping_address || '',
      shipping_postal_code: this.item.shipping_postal_code || '',
      shipping_city: this.item.shipping_city || '',
      shipping_country: this.item.shipping_country || '',
      notes: this.item.notes || '',
    };
  }

  private handleError(e: any, fallbackMessage: string, stopLoading: boolean = true) {
    if (stopLoading) {
      this.loading = false;
    }
    this.itemsLoading = false;

    if (e.status === 422 && e.error?.error) {
      const messages: string[] = [];
      const errors = e.error.error;
      for (const field in errors) {
        if (errors[field].length) {
          messages.push(...errors[field]);
        }
      }
      this.errorMessage = messages.join(' ');
      return;
    }

    this.errorMessage = fallbackMessage;
  }
}
